'use client';

import React, { useState, useEffect } from 'react';
import { App_text } from '@/settings/AppText';

/**
 * A reusable image component with fallback support
 *
 * @param {Object} props
 * @param {string} props.src - Primary image source
 * @param {string} props.fallbackSrc - Fallback image source (optional)
 * @param {string} props.alt - Alt text for the image
 * @param {string} props.className - CSS classes
 * @param {Object} props.rest - Any other props to pass to the img element
 */
export default function ImageWithFallback({ src, fallbackSrc, alt, className, fill, priority, quality, loading, sizes, ...rest }) {
    // Get appropriate fallback image based on image type if none provided
    const getFallbackImage = () => {
        if (fallbackSrc) return fallbackSrc;

        // Default to card type thumbnail if no specific fallback provided
        return App_text.defaults.images.card_type_thumbnail;
    };

    // Initial state setup
    const initialSrc = src || getFallbackImage();
    const [imgSrc, setImgSrc] = useState(initialSrc);
    const [imgError, setImgError] = useState(false);

    // Reset when src changes
    useEffect(() => {
        // Only reset if we're not already showing the fallback
        if (!imgError) {
            setImgSrc(src || getFallbackImage());
        }
    }, [src]);

    const handleError = () => {
        // Only update if we haven't already shown the fallback
        if (!imgError) {
            console.log('Image failed to load, using fallback:', src);
            setImgError(true);
            setImgSrc(getFallbackImage());
        }
    };

    // Prepare styles for "fill" behavior if requested
    const fillStyles = fill
        ? {
              position: 'absolute',
              height: '100%',
              width: '100%',
              inset: 0,
              color: 'transparent',
          }
        : {};

    const combinedStyles = { ...fillStyles, ...(rest.style || {}) };

    return (
        <img
            src={imgSrc}
            alt={alt || 'Image'}
            className={className}
            onError={handleError}
            style={combinedStyles}
            {...rest}
        />
    );
}
